import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { StanDataRowGroup } from '@/components';
import type { FlowSummary } from '@/lib/api/email-flows/types';
import { BroadcastItem } from './BroadcastItem';
import { useBroadcastActions } from '../hooks/useBroadcastActions';
import { useBroadcastModals } from '../hooks/useBroadcastModals';
import { BROADCAST_HEADER_COLUMNS, getGridTemplate } from '../constants/gridColumns';
import { DeleteFlowModal } from '../../components';

interface BroadcastListProps {
    broadcasts: FlowSummary[];
}

export const BroadcastList = ({ broadcasts }: BroadcastListProps) => {
    const gridTemplate = getGridTemplate();
    const [newlyDuplicatedId, setNewlyDuplicatedId] = useState<number | null>(null);
    const prevCountRef = useRef(broadcasts.length);

    const { confirmDelete, deletingBroadcastId } = useBroadcastActions();
    const { delete: deleteModalState } = useBroadcastModals();

    const handleNewBroadcastDetection = useEffectEvent((broadcastList: FlowSummary[]) => {
        const currentCount = broadcastList.length;
        const prevCount = prevCountRef.current;

        if (currentCount > prevCount) {
            const newBroadcast = broadcastList[0];
            if (newBroadcast) {
                setNewlyDuplicatedId(newBroadcast.flow_id);
                setTimeout(() => {
                    setNewlyDuplicatedId(null);
                }, 500); // Match animation duration
            }
        }

        prevCountRef.current = currentCount;
    });

    // Detect when a new broadcast is added (duplication) and trigger animation
    useEffect(() => {
        handleNewBroadcastDetection(broadcasts);
    }, [broadcasts]);

    return (
        <>
            <StanDataRowGroup headerColumns={BROADCAST_HEADER_COLUMNS} maxActionButtonCount={1}>
                <div className='space-y-4 md:space-y-6'>
                    {broadcasts.map(broadcast => (
                        <BroadcastItem
                            key={broadcast.flow_id}
                            broadcast={broadcast}
                            gridTemplate={gridTemplate}
                            isBeingDeleted={deletingBroadcastId === broadcast.flow_id}
                            isNewlyDuplicated={newlyDuplicatedId === broadcast.flow_id}
                            onDelete={deleteModalState.open}
                        />
                    ))}
                </div>
            </StanDataRowGroup>

            <DeleteFlowModal
                isOpen={deleteModalState.isOpen}
                flowName={deleteModalState.flowName}
                onConfirm={() => confirmDelete(deleteModalState.flowId, deleteModalState.close)}
                onCancel={deleteModalState.close}
                type='broadcast'
            />
        </>
    );
};
